import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Button,
  DataTable,
  DataTablePagination,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@cargomatic/ui';
import { MoreHorizontal } from 'lucide-react';
import _ from 'lodash';
import SessionStore from 'stores/SessionStore';
import Alert from 'components/ui/Alert';
import WebAPIUtils from 'utils/AccessorialRequestWebAPIUtils';
import LocationWebAPIUtils from 'utils/LocationWebAPIUtils';
import MarketsAPIUtils from 'utils/MarketsAPIUtils';
import { showToast } from 'stores/ToastStore';
import { findManyUsers } from 'utils/UserWebAPIUtils';
import { validateDocumentFilesSizes } from 'utils/documents';
import {
  BLANK_ACCESSORIAL_RATE,
  BLANK_CUSTOMER,
  DEFAULT_SEARCH_CUSTOMER_TYPE,
  STATUS,
  VALID_CUSTOMER_TYPE,
} from 'constants/AccessorialRates';
import { AddAccessorialCustomersMarketModal } from '../../carrier/modals/AddAccessorialLaneModal';
import { AddAccessorialRatesModal } from '../../carrier/modals/AddAccessorialsModal';
import { LaneRateAuditTrailModal as AuditTrailModal } from '../modals/LaneRateAuditTrailModal';
import { accessorialRatesFilesMapper, customerDataMapper } from '../helpers';
import { fetchExistingAccessorialRates } from '../../carrier/modals/helpers/accessorial-rate-helpers';
import { getAccessorialRatesColumns } from './AccessorialRatesColumns';

const PER_PAGE = 20;

export function AccessorialRatesTable(props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({
    pageSize: PER_PAGE,
    pageIndex: 0,
  });
  const [_accessorialRates, setAccessorialRates] = useState([
    BLANK_ACCESSORIAL_RATE,
  ]);
  const [missingRequiredFields, setMissingRequiredFields] = useState(false);
  const [disableFooterButtons, setDisableFooterButtons] = useState(true);
  const [_filesUploaded, setFilesUploaded] = useState();
  const [rateAudit, setRateAudit] = useState();
  const [showAuditTrailModal, setShowAuditTrailModal] = useState(false);
  const [showAddCustomersMarketModal, setShowAddCustomersMarketModal] =
    useState(false);
  const [showAddAccessorialRatesModal, setShowAddAccessorialRatesModal] =
    useState(false);
  const [showEditAccessorialsModal, setShowEditAccessorialsModal] =
    useState(false);
  const [items, setItems] = useState([]);
  const [marketsList, setMarketsList] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [loadingConfirmation, setLoadingConfirmation] = useState(false);
  const [searchCustomerType, setSearchCustomerType] = useState(
    DEFAULT_SEARCH_CUSTOMER_TYPE,
  );
  const [market, setMarket] = useState('');
  const [customerData, setCustomerData] = useState([]);
  const [isCustomerTypeValid, setIsCustomerTypeValid] = useState(true);
  const [areCustomersTheSameType, setAreCustomersTheSameType] = useState(true);
  const [areCustomersNaiveToMarket, setAreCustomersNaiveToMarket] =
    useState(true);
  const [duplicateCustomers, setDuplicateCustomers] = useState(false);
  const [
    customersWithMarketConfirmedRates,
    setCustomersWithMarketConfirmedRates,
  ] = useState(new Set([]));
  const [isFetchingExistingCustomers, setIsFetchingExistingCustomers] =
    useState(false);

  // --- Open add modal from parent ----------------------------------------
  useEffect(() => {
    if (props.addModalOpen) {
      setShowAddCustomersMarketModal(true);
    }
  }, [props.addModalOpen]);

  // --- Data fetching -----------------------------------------------------
  function setFilterQueryParameters() {
    const {
      customer,
      status,
      market,
      customerType,
      autoApply,
      accessorialType,
      legType,
    } = props;
    const filterString = {};
    if (customer) filterString.customer = customer;
    if (market) filterString.market = market;
    if (status) filterString.status = status;
    if (customerType) filterString.customerType = customerType;
    if (typeof autoApply === 'boolean') filterString.autoApply = autoApply;
    if (accessorialType) filterString.accessorialtype = accessorialType;
    if (legType) filterString.legType = legType;
    return filterString;
  }

  async function getAccessorialRates() {
    const filterString = setFilterQueryParameters();
    const skip = pagination.pageIndex * pagination.pageSize;

    const queryParams = new URLSearchParams({
      per_page: String(pagination.pageSize),
      skip,
      ...filterString,
    });

    if (sorting.length > 0) {
      queryParams.append('sortColumn', sorting[0].id);
      queryParams.append('sortDirection', sorting[0].desc ? 'DESC' : 'ASC');
    }

    try {
      const response = await WebAPIUtils.getLaneAccessorialRatesV2(queryParams);
      if (response) {
        const { laneAccessorialRates, count } = response;
        setItems(laneAccessorialRates);
        setTotal(count);
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const getMarketsList = async () => {
      try {
        const result = await MarketsAPIUtils.getMarkets();
        if (result.entity.markets) {
          const marketNames = _.map(result.entity.markets, (m) => ({
            _id: m.id,
            name: m.name,
            slug: m.name,
          }));
          setMarketsList(marketNames);
        }
      } catch (e) {
        console.error('failed to fetch market values: ', e);
      }
    };
    getMarketsList();
    handleCustomerMarketValidation();
  }, []);

  // Track previous filter values so we can distinguish filter changes (which
  // must reset pagination) from pagination/sort changes (which must fetch
  // directly). Without this split, changing a filter while off page 1 would
  // issue a stale fetch before the reset-to-page-0 fetch, creating a race.
  const prevFiltersRef = useRef({
    customer: props.customer,
    customerType: props.customerType,
    status: props.status,
    autoApply: props.autoApply,
    accessorialType: props.accessorialType,
    market: props.market,
    legType: props.legType,
  });

  useEffect(() => {
    const nextFilters = {
      customer: props.customer,
      customerType: props.customerType,
      status: props.status,
      autoApply: props.autoApply,
      accessorialType: props.accessorialType,
      market: props.market,
      legType: props.legType,
    };
    const filtersChanged =
      JSON.stringify(prevFiltersRef.current) !== JSON.stringify(nextFilters);
    prevFiltersRef.current = nextFilters;

    if (filtersChanged && pagination.pageIndex !== 0) {
      // Reset to page 0; the resulting pageIndex change will re-trigger this
      // effect and fetch with the new filter + pageIndex=0 in one request.
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      return;
    }
    setLoading(true);
    getAccessorialRates();
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    sorting,
    props.customer,
    props.customerType,
    props.status,
    props.autoApply,
    props.accessorialType,
    props.market,
    props.legType,
  ]);

  useEffect(() => {
    handleCustomerMarketValidation();
  }, [
    customerData,
    market,
    searchCustomerType,
    customersWithMarketConfirmedRates,
  ]);

  useEffect(() => {
    const fetchExistingCustomers = async () => {
      if (searchCustomerType !== 'customer' && market) {
        setIsFetchingExistingCustomers(true);
        const customerType = searchCustomerType.slice(0, -1).toLowerCase();
        try {
          const laneAccessorialRates = await fetchExistingAccessorialRates({
            status: STATUS.CONFIRMED,
            market,
            customerType,
            paginate: false,
          });
          const uniqueCustomers = new Set(
            laneAccessorialRates.map((ar) => ar.customer),
          );
          setCustomersWithMarketConfirmedRates(uniqueCustomers);
        } catch (_e) {
          showToast(
            t('REQUEST_ERROR'),
            t('LOADING_ACCESSORIALS_ERROR'),
            'error',
          );
        } finally {
          setIsFetchingExistingCustomers(false);
        }
      }
    };
    fetchExistingCustomers();
  }, [market, searchCustomerType]);

  // --- Row actions -------------------------------------------------------
  function handleAuditClick(item) {
    setRateAudit(item);
    setShowAuditTrailModal(true);
  }

  const handleAccessorialRateEdit = (accessorialRate) => {
    const { name, customer, customerType, market } = accessorialRate;
    setCustomerData([{ name, customer, customerType }]);
    setMarket(market);
    setShowEditAccessorialsModal(true);
  };

  // --- Customer/market modal logic (unchanged) ---------------------------
  const resetErrors = () => {
    setAreCustomersNaiveToMarket(true);
    setAreCustomersTheSameType(true);
    setIsCustomerTypeValid(true);
    setDuplicateCustomers(false);
  };

  const handleCustomersChange = (nextCustomers) => {
    const invalid = nextCustomers.find(
      (c) =>
        c.customerType && !VALID_CUSTOMER_TYPE.hasOwnProperty(c.customerType),
    );
    if (invalid) {
      setIsCustomerTypeValid(false);
      return;
    }
    setIsCustomerTypeValid(true);

    setCustomerData(nextCustomers);

    if (!nextCustomers.length) {
      setSearchCustomerType(DEFAULT_SEARCH_CUSTOMER_TYPE);
      resetErrors();
      return;
    }

    const firstType = nextCustomers.find((c) => c.customerType)?.customerType;
    if (firstType) {
      const nextSearchType = `${firstType.toLocaleLowerCase()}s`;
      if (searchCustomerType !== nextSearchType) {
        setSearchCustomerType(nextSearchType);
      }
    }
  };

  const handleMarketChange = (m) => {
    setMarket(m.slug);
  };

  const handleCustomerMarketValidation = () => {
    // Derive all flags locally, then commit each setter exactly once.
    // The previous implementation mutated React state inside the loop,
    // producing order-dependent UI — flags like areCustomersTheSameType
    // never recovered to true after the user fixed a mismatch.
    let areRequiredFieldsMissing = !market || !customerData.length;
    let nextDuplicate = false;
    let nextNaive = true;
    let nextSameType = true;

    const customerIds = {};
    const currSearchCustomerType = searchCustomerType.slice(0, -1);

    for (const currCustomer of customerData) {
      const currCustomerId = currCustomer.customer;

      if (customerIds.hasOwnProperty(currCustomerId)) {
        nextDuplicate = true;
      } else {
        customerIds[currCustomerId] = currCustomerId;
      }

      if (!currCustomer.customer) {
        areRequiredFieldsMissing = true;
      }

      if (customerData.length > 1) {
        if (customersWithMarketConfirmedRates.has(currCustomer.customer)) {
          nextNaive = false;
        }
        if (
          currCustomer.customerType &&
          currCustomer.customerType !== currSearchCustomerType
        ) {
          // Preserves original behavior: a type mismatch suppresses the
          // missing-fields flag so only the type error surfaces.
          areRequiredFieldsMissing = false;
          nextSameType = false;
        }
      }
    }

    setDuplicateCustomers(nextDuplicate);
    setAreCustomersNaiveToMarket(nextNaive);
    setAreCustomersTheSameType(nextSameType);
    setMissingRequiredFields(areRequiredFieldsMissing);
  };

  useEffect(() => {
    const areAllDataValid = Boolean(
      areCustomersNaiveToMarket &&
        areCustomersTheSameType &&
        !missingRequiredFields &&
        !duplicateCustomers,
    );
    setDisableFooterButtons(!areAllDataValid);
  }, [
    missingRequiredFields,
    areCustomersTheSameType,
    areCustomersNaiveToMarket,
    duplicateCustomers,
  ]);

  const onZoneBlur = async (zip) => LocationWebAPIUtils.zipToCityState(zip);

  const handleCloseModal = (clear) => {
    if (clear) {
      setAccessorialRates([BLANK_CUSTOMER]);
      setCustomerData([BLANK_CUSTOMER]);
      setMarket('');
      resetErrors();
    }
    setShowAddCustomersMarketModal(false);
    setShowAddAccessorialRatesModal(false);
    setShowEditAccessorialsModal(false);
    props.onCloseAddModal?.();
  };

  async function fetchCustomerEmail(availableAccessorialRates) {
    let customers = [];
    const uniqueCustomerIds = [
      ...new Set(availableAccessorialRates.map((ar) => ar.customer)),
    ];
    const userData = await findManyUsers(uniqueCustomerIds);
    const userDataItems = userData?.entity?.items;
    if (userDataItems.length) {
      customers = userDataItems.reduce(
        (acc, val) =>
          acc.concat(customerDataMapper(val, availableAccessorialRates)),
        [],
      );
    } else {
      customers = availableAccessorialRates;
    }
    return customers;
  }

  const handleComplete = (files) => setFilesUploaded(files);

  async function handleSave(laneAccessorialRates) {
    setLoadingConfirmation(true);
    try {
      setMissingRequiredFields(false);
      const mapped = await fetchCustomerEmail(laneAccessorialRates);
      const result = await WebAPIUtils.updateLaneAccessorialRates(mapped);
      if (result?.success) {
        showToast(
          t('REQUEST_SUCCESS'),
          t('ACCESSORIAL_LANE_RATE_SUCCESSFULLY_SUBMITTED'),
          'success',
        );
        await getAccessorialRates();
      } else {
        showToast(t('REQUEST_ERROR'), t('LANE_RATE_UPDATE_ERROR'), 'error');
      }
      setLoadingConfirmation(false);
      setShowAddCustomersMarketModal(false);
      setShowAddAccessorialRatesModal(false);
    } catch (e) {
      setLoadingConfirmation(false);
      showToast(t('REQUEST_ERROR'), t('LANE_RATE_UPDATE_ERROR'), 'error');
      console.error(e);
    }
  }

  const handleDropFiles = (index) => (files) => {
    setUploadingFiles(true);
    try {
      validateDocumentFilesSizes(files);
      setCustomerData((prev) => {
        const next = [...prev];
        next[index].files = files;
        return next;
      });
      const fileName = files[0]?.name || t('DOCUMENT');
      showToast(
        t('REQUEST_SUCCESS'),
        `${fileName} ${t('UPLOADED')}`,
        'success',
      );
    } catch (_e) {
      // validateDocumentFileSizes displays a toast
    }
    setUploadingFiles(false);
  };

  async function handleSubmitAlreadyConfirmed(payloads) {
    await Promise.all(
      payloads.map(async (payload) => {
        try {
          await WebAPIUtils.alreadyConfirmedAccessorialRates(
            payload.accessorialRates,
            payload.files,
          );
        } catch (_e) {
          showToast(t('REQUEST_ERROR'), t('LANE_RATE_UPDATE_ERROR'), 'error');
          throw new Error(t('LANE_RATE_UPDATE_ERROR'));
        }
      }),
    );
    return { success: true };
  }

  async function handleSubmitAccessorialRates(laneAccessorialRates) {
    setUploadingFiles(true);
    setLoadingConfirmation(true);
    try {
      const customers = await fetchCustomerEmail(laneAccessorialRates);
      const mapped = accessorialRatesFilesMapper(customers, customerData);
      const payloadsArray = Object.values(mapped);
      await handleSubmitAlreadyConfirmed(payloadsArray);
      showToast(
        t('REQUEST_SUCCESS'),
        t('ACCESSORIAL_LANE_RATE_SUCCESSFULLY_SUBMITTED'),
        'success',
      );
      await getAccessorialRates();
      handleCloseModal(true);
    } catch (_e) {
      showToast(t('REQUEST_ERROR'), t('LANE_RATE_UPDATE_ERROR'), 'error');
    } finally {
      setLoadingConfirmation(false);
      setUploadingFiles(false);
    }
  }

  // --- Tanstack table setup ----------------------------------------------
  const baseColumns = useMemo(() => getAccessorialRatesColumns(), []);

  const columnsWithActions = useMemo(() => {
    const deferred = (fn) => () => requestAnimationFrame(fn);
    return baseColumns.concat({
      id: 'actions',
      header: () => null,
      cell: ({ row }) => {
        const item = row.original;
        const docUrl = item.document?.signedUrl;
        return (
          <div className="text-right">
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="w-8 h-8 p-0">
                  <span className="sr-only">{t('OPEN_MENU')}</span>
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                {props.canUpdate && (
                  <DropdownMenuItem
                    onClick={deferred(() => handleAccessorialRateEdit(item))}
                  >
                    Edit
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={deferred(() => handleAuditClick(item))}
                >
                  View Audit Trail
                </DropdownMenuItem>
                {docUrl && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={deferred(() =>
                        window.open(docUrl, '_blank', 'noopener,noreferrer'),
                      )}
                    >
                      View Document
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
      enableSorting: false,
    });
  }, [baseColumns, props.canUpdate]);

  const table = useReactTable({
    columns: columnsWithActions,
    data: items,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    manualSorting: true,
    rowCount: total,
    state: {
      sorting,
      pagination,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
  });

  // --- Render ------------------------------------------------------------
  if (!SessionStore.isAdmin()) {
    return (
      <div className="p-4">
        <Alert
          dataTestId="Alert-8cfef03d"
          title={t('NO_PERMISSION_TO_VIEW_PAGE')}
          type="error"
        />
      </div>
    );
  }

  return (
    <div>
      <DataTable table={table} isLoading={loading} />
      {!loading && <DataTablePagination table={table} />}

      <AuditTrailModal
        show={showAuditTrailModal}
        rate={rateAudit}
        onHide={() => setShowAuditTrailModal(false)}
        type="laneAccessorialRates"
      />
      {showAddCustomersMarketModal && (
        <AddAccessorialCustomersMarketModal
          showModal={showAddCustomersMarketModal}
          showAddAccessorialModal={showAddCustomersMarketModal}
          setShowAddAccessorialsModal={setShowAddAccessorialRatesModal}
          marketsArray={marketsList}
          customerData={customerData}
          searchCustomerType={searchCustomerType}
          onCustomersChange={handleCustomersChange}
          onClose={handleCloseModal}
          onMarketChange={handleMarketChange}
          market={market}
          onZoneBlur={onZoneBlur}
          missingRequiredFields={missingRequiredFields}
          disableButtons={disableFooterButtons}
          isCustomerTypeValid={isCustomerTypeValid}
          areCustomersNaiveToMarket={areCustomersNaiveToMarket}
          areCustomersTheSameType={areCustomersTheSameType}
          isFetchingExistingCustomers={isFetchingExistingCustomers}
          duplicateCustomers={duplicateCustomers}
        />
      )}
      {showAddAccessorialRatesModal && (
        <AddAccessorialRatesModal
          showModal={showAddAccessorialRatesModal}
          onClose={handleCloseModal}
          customerData={customerData}
          market={market}
          handleDropFiles={handleDropFiles}
          handleSubmit={handleSubmitAccessorialRates}
          onComplete={handleComplete}
          handleSave={handleSave}
          loadingConfirmation={loadingConfirmation}
          setLoadingConfirmation={setLoadingConfirmation}
          uploadingFiles={uploadingFiles}
          disableButtons={disableFooterButtons}
          onBack={() => {
            setShowAddAccessorialRatesModal(false);
            setShowAddCustomersMarketModal(true);
          }}
        />
      )}
      {showEditAccessorialsModal && (
        <AddAccessorialRatesModal
          showModal={showEditAccessorialsModal}
          isEdit
          handleDropFiles={handleDropFiles}
          handleSubmit={handleSubmitAccessorialRates}
          customerData={customerData}
          market={market}
          onComplete={handleComplete}
          handleSave={handleSave}
          setAccessorialRates={setAccessorialRates}
          loadingConfirmation={loadingConfirmation}
          setLoadingConfirmation={setLoadingConfirmation}
          onClose={handleCloseModal}
          uploadingFiles={uploadingFiles}
          disableButtons={disableFooterButtons}
          onBack={() => {
            setShowEditAccessorialsModal(false);
            setMarket('');
            setCustomerData([BLANK_CUSTOMER]);
          }}
        />
      )}
    </div>
  );
}
